import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Space, Table } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { createParser, parseAsString, useQueryState } from "nuqs";
import { ColumnManager } from "./ColumnManager";

export type PersistedColumnConfig = {
  key: string;
  visible: boolean;
};

type NormalizedColumn<T> = {
  key: string;
  title: string;
  column: ColumnsType<T>[number];
};

interface MeterReadingsTableProps<T extends object> {
  tableKey: string;
  rowKey: TableProps<T>["rowKey"];
  columns: ColumnsType<T>;
  tableProps?: Partial<TableProps<T>>;
  dataSource?: T[];
  loading?: boolean;
  pagination?: TableProps<T>["pagination"];
  scroll?: TableProps<T>["scroll"];
  expandable?: TableProps<T>["expandable"];
  onChange?: TableProps<T>["onChange"];
  defaultColumns?: PersistedColumnConfig[];
  columnStateMode?: "memory" | "url";
  lockedColumnKeys?: string[];
}

const extractColumnKey = <T extends object>(column: ColumnsType<T>[number]): string | null => {
  if (!column) {
    return null;
  }

  if ("key" in column && column.key != null) {
    return String(column.key);
  }

  if ("dataIndex" in column && column.dataIndex != null) {
    if (Array.isArray(column.dataIndex)) {
      return column.dataIndex.join(".");
    }
    return String(column.dataIndex);
  }

  return null;
};

const toTitle = <T extends object>(column: ColumnsType<T>[number]): string => {
  if ("title" in column && typeof column.title === "string") {
    return column.title;
  }
  return extractColumnKey(column) ?? "Column";
};

const sanitizeQueryKey = (value: string): string => value.replace(/[^a-zA-Z0-9_-]/g, "_");

const hashQueryKey = (value: string): string => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const areColumnConfigsEqual = (
  left: PersistedColumnConfig[],
  right: PersistedColumnConfig[]
): boolean =>
  left.length === right.length &&
  left.every(
    (item, index) => item.key === right[index]?.key && item.visible === right[index]?.visible
  );

const normalizeColumnConfig = (
  candidate: PersistedColumnConfig[],
  defaults: PersistedColumnConfig[]
): PersistedColumnConfig[] => {
  const defaultVisibility = new Map(defaults.map((item) => [item.key, item.visible]));
  const candidateVisibility = new Map(candidate.map((item) => [item.key, item.visible]));

  const orderedKeys: string[] = [];
  candidate.forEach((item) => {
    if (defaultVisibility.has(item.key) && !orderedKeys.includes(item.key)) {
      orderedKeys.push(item.key);
    }
  });
  defaults.forEach((item) => {
    if (!orderedKeys.includes(item.key)) {
      orderedKeys.push(item.key);
    }
  });

  return orderedKeys.map((key) => ({
    key,
    visible: candidateVisibility.has(key)
      ? Boolean(candidateVisibility.get(key))
      : Boolean(defaultVisibility.get(key)),
  }));
};

const COLUMN_ALIAS_OVERRIDES: Record<string, string> = {
  name: "n",
  status: "s",
  latest_active_value: "a",
  latest_reactive_value: "r",
  latest_readout_value: "a",
  latest_readout_value_dual: "h",
  latest_active_timestamp: "t",
  latest_readout_timestamp: "t",
  latest_snapshot_timestamp: "t",
  primary_value: "p",
  media: "m",
  uuid: "u",
  address: "d",
  serial: "i",
  hot: "o",
  battery_value: "b",
};

export const buildAliasMaps = (
  defaults: PersistedColumnConfig[]
): { aliasByKey: Map<string, string>; keyByAlias: Map<string, string> } => {
  const aliasByKey = new Map<string, string>();
  const keyByAlias = new Map<string, string>();
  defaults.forEach((column, index) => {
    const preferredAlias = COLUMN_ALIAS_OVERRIDES[column.key];
    let alias = preferredAlias && !keyByAlias.has(preferredAlias)
      ? preferredAlias
      : `c${index.toString(36)}`;
    if (keyByAlias.has(alias)) {
      let fallbackIndex = index;
      while (keyByAlias.has(alias)) {
        alias = `c${fallbackIndex.toString(36)}`;
        fallbackIndex += 1;
      }
    }
    aliasByKey.set(column.key, alias);
    keyByAlias.set(alias, column.key);
  });
  return { aliasByKey, keyByAlias };
};

const parseAliasColumnConfigFromQuery = (
  value: string,
  keyByAlias: Map<string, string>
): PersistedColumnConfig[] => {
  const normalized = value.trim();
  const withoutBrackets =
    normalized.startsWith("[") && normalized.endsWith("]")
      ? normalized.slice(1, -1)
      : normalized;
  if (!withoutBrackets) {
    return [];
  }

  return withoutBrackets
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => {
      const isVisible = token === token.toUpperCase();
      const alias = token.toLowerCase();
      const key = keyByAlias.get(alias);
      if (!key) {
        return null;
      }
      return {
        key,
        visible: isVisible,
      };
    })
    .filter((item): item is PersistedColumnConfig => item !== null);
};

const encodeAliasColumnConfigForQuery = (
  config: PersistedColumnConfig[],
  aliasByKey: Map<string, string>
): string => {
  const tokens = config
    .map((column) => {
      const alias = aliasByKey.get(column.key);
      if (!alias) {
        return null;
      }
      return column.visible ? alias.toUpperCase() : alias;
    })
    .filter((item): item is string => Boolean(item));
  return `[${tokens.join(",")}]`;
};

const parseLegacyColumnConfigFromQuery = (value: string): PersistedColumnConfig[] => {
  return value
    .split(",")
    .map((entry) => {
      const dividerIndex = entry.lastIndexOf(":");
      if (dividerIndex <= 0) {
        return null;
      }
      const key = decodeURIComponent(entry.slice(0, dividerIndex));
      const flag = entry.slice(dividerIndex + 1);
      if (!key || (flag !== "0" && flag !== "1")) {
        return null;
      }
      return { key, visible: flag === "1" };
    })
    .filter((item): item is PersistedColumnConfig => item !== null);
};

const decodeIndices = (value: string, max: number): number[] | null => {
  if (!value) {
    return [];
  }
  if (value.length % 2 !== 0) {
    return null;
  }
  const parsed: number[] = [];
  for (let i = 0; i < value.length; i += 2) {
    const token = value.slice(i, i + 2);
    const index = Number.parseInt(token, 36);
    if (!Number.isFinite(index) || index < 0 || index >= max) {
      return null;
    }
    parsed.push(index);
  }
  return parsed;
};

const parseCompactColumnConfigFromQuery = (
  value: string,
  defaults: PersistedColumnConfig[]
): PersistedColumnConfig[] | null => {
  const [orderPart, hiddenPart = ""] = value.split(".", 2);
  const orderIndices = decodeIndices(orderPart, defaults.length);
  if (!orderIndices || orderIndices.length === 0) {
    return null;
  }

  const dedupedOrder: number[] = [];
  orderIndices.forEach((index) => {
    if (!dedupedOrder.includes(index)) {
      dedupedOrder.push(index);
    }
  });

  const hiddenIndices = decodeIndices(hiddenPart, defaults.length);
  if (!hiddenIndices) {
    return null;
  }
  const hiddenSet = new Set(hiddenIndices);

  return dedupedOrder.map((index) => ({
    key: defaults[index].key,
    visible: !hiddenSet.has(index),
  }));
};

export function MeterReadingsTable<T extends object>({
  tableKey,
  rowKey,
  columns,
  tableProps,
  dataSource,
  loading,
  pagination,
  scroll = { x: 1200 },
  expandable,
  onChange,
  defaultColumns,
  columnStateMode = "memory",
  lockedColumnKeys = [],
}: MeterReadingsTableProps<T>): JSX.Element {
  const compactQueryKey = "cols";
  const legacyQueryKey = useMemo(() => `cols_${sanitizeQueryKey(tableKey)}`, [tableKey]);
  const oldCompactQueryKey = useMemo(() => `c_${hashQueryKey(tableKey)}`, [tableKey]);

  const lockedKeySet = useMemo(() => new Set(lockedColumnKeys), [lockedColumnKeys]);

  const normalizedColumns = useMemo<NormalizedColumn<T>[]>(() => {
    return columns
      .map((column) => {
        const key = extractColumnKey(column);
        if (!key) {
          return null;
        }
        return {
          key,
          title: toTitle(column),
          column,
        };
      })
      .filter((item): item is NormalizedColumn<T> => item !== null);
  }, [columns]);

  const orderedDefaults = useMemo<PersistedColumnConfig[]>(() => {
    const configurableColumns = normalizedColumns.filter((column) => !lockedKeySet.has(column.key));
    const visibilityByKey = new Map(
      (defaultColumns ?? []).map((column) => [column.key, column.visible])
    );
    const baseDefaults = configurableColumns.map((column) => ({
      key: column.key,
      visible: visibilityByKey.get(column.key) ?? true,
    }));

    if (!defaultColumns?.length) {
      return baseDefaults;
    }

    return [
      ...defaultColumns
        .map((column) => baseDefaults.find((item) => item.key === column.key))
        .filter((item): item is PersistedColumnConfig => Boolean(item)),
      ...baseDefaults.filter((item) => !defaultColumns.some((column) => column.key === item.key)),
    ];
  }, [defaultColumns, normalizedColumns, lockedKeySet]);

  const { aliasByKey, keyByAlias } = useMemo(
    () => buildAliasMaps(orderedDefaults),
    [orderedDefaults]
  );

  const compactColumnConfigParser = useMemo(
    () =>
      createParser<PersistedColumnConfig[]>({
        parse: (value) => {
          const parsedFromAlias = parseAliasColumnConfigFromQuery(value, keyByAlias);
          if (parsedFromAlias.length > 0) {
            return normalizeColumnConfig(parsedFromAlias, orderedDefaults);
          }

          const parsedFromCompact = parseCompactColumnConfigFromQuery(value, orderedDefaults);
          if (!parsedFromCompact || parsedFromCompact.length === 0) {
            return null;
          }
          return normalizeColumnConfig(parsedFromCompact, orderedDefaults);
        },
        serialize: (value) => encodeAliasColumnConfigForQuery(value, aliasByKey),
        eq: areColumnConfigsEqual,
      }).withOptions({ history: "replace" }),
    [aliasByKey, keyByAlias, orderedDefaults]
  );

  const [urlColumnConfig, setUrlColumnConfig] = useQueryState(
    compactQueryKey,
    compactColumnConfigParser
  );
  const [oldUrlColumnConfig, setOldUrlColumnConfig] = useQueryState(
    oldCompactQueryKey,
    parseAsString.withOptions({ history: "replace" })
  );
  const [legacyColumnConfig, setLegacyColumnConfig] = useQueryState(
    legacyQueryKey,
    parseAsString.withOptions({ history: "replace" })
  );

  const [columnConfig, setColumnConfig] = useState<PersistedColumnConfig[]>(() => orderedDefaults);
  const pendingUrlSyncRef = useRef<PersistedColumnConfig[] | null>(null);
  const pendingPersistRef = useRef<PersistedColumnConfig[] | null>(null);

  useEffect(() => {
    if (columnStateMode !== "url" || urlColumnConfig) {
      return;
    }
    if (oldUrlColumnConfig) {
      const parsedOldCompact = parseCompactColumnConfigFromQuery(oldUrlColumnConfig, orderedDefaults);
      if (parsedOldCompact && parsedOldCompact.length > 0) {
        void setUrlColumnConfig(normalizeColumnConfig(parsedOldCompact, orderedDefaults), {
          history: "replace",
        });
      }
      void setOldUrlColumnConfig(null, { history: "replace" });
      return;
    }
    if (legacyColumnConfig) {
      const parsedLegacy = parseLegacyColumnConfigFromQuery(legacyColumnConfig);
      if (parsedLegacy.length > 0) {
        void setUrlColumnConfig(normalizeColumnConfig(parsedLegacy, orderedDefaults), {
          history: "replace",
        });
      }
      void setLegacyColumnConfig(null, { history: "replace" });
    }
  }, [
    columnStateMode,
    legacyColumnConfig,
    oldUrlColumnConfig,
    orderedDefaults,
    setLegacyColumnConfig,
    setOldUrlColumnConfig,
    setUrlColumnConfig,
    urlColumnConfig,
  ]);

  useEffect(() => {
    if (columnStateMode !== "url" || !legacyColumnConfig) {
      return;
    }
    void setLegacyColumnConfig(null, { history: "replace" });
  }, [
    columnStateMode,
    orderedDefaults,
    setLegacyColumnConfig,
    legacyColumnConfig,
  ]);

  useEffect(() => {
    if (columnStateMode !== "url" || !oldUrlColumnConfig) {
      return;
    }
    void setOldUrlColumnConfig(null, { history: "replace" });
  }, [
    columnStateMode,
    oldUrlColumnConfig,
    setOldUrlColumnConfig,
  ]);

  useEffect(() => {
    if (columnStateMode !== "url") {
      pendingUrlSyncRef.current = null;
      pendingPersistRef.current = null;
      setColumnConfig((current) =>
        // Keep in-memory user changes across rerenders while still reconciling
        // against any new/removed columns from the latest defaults.
        (() => {
          const nextConfig = normalizeColumnConfig(current, orderedDefaults);
          return areColumnConfigsEqual(current, nextConfig) ? current : nextConfig;
        })()
      );
      return;
    }

    const nextConfig = normalizeColumnConfig(urlColumnConfig ?? orderedDefaults, orderedDefaults);

    if (pendingUrlSyncRef.current) {
      if (areColumnConfigsEqual(nextConfig, pendingUrlSyncRef.current)) {
        pendingUrlSyncRef.current = null;
      } else {
        return;
      }
    }

    setColumnConfig((current) =>
      areColumnConfigsEqual(current, nextConfig) ? current : nextConfig
    );
  }, [columnStateMode, orderedDefaults, urlColumnConfig]);

  const columnMap = useMemo(() => {
    return new Map(normalizedColumns.map((column) => [column.key, column]));
  }, [normalizedColumns]);

  const visibleColumns = useMemo(() => {
    const configurableVisible = columnConfig
      .filter((entry) => entry.visible)
      .map((entry) => columnMap.get(entry.key)?.column)
      .filter((column): column is ColumnsType<T>[number] => Boolean(column));
    const lockedColumns = normalizedColumns
      .filter((entry) => lockedKeySet.has(entry.key))
      .map((entry) => entry.column);

    return [...configurableVisible, ...lockedColumns];
  }, [columnConfig, columnMap, normalizedColumns, lockedKeySet]);

  const managerColumns = useMemo(() => {
    return columnConfig
      .map((entry) => {
        const source = columnMap.get(entry.key);
        if (!source) {
          return null;
        }
        return {
          key: entry.key,
          title: source.title,
          visible: entry.visible,
        };
      })
      .filter((item): item is { key: string; title: string; visible: boolean } => Boolean(item));
  }, [columnConfig, columnMap]);

  const persistColumnConfig = useCallback(
    (nextConfig: PersistedColumnConfig[]): void => {
      if (columnStateMode !== "url") {
        return;
      }
      if (areColumnConfigsEqual(nextConfig, orderedDefaults)) {
        void setUrlColumnConfig(null);
      } else {
        void setUrlColumnConfig(nextConfig);
      }
    },
    [columnStateMode, orderedDefaults, setUrlColumnConfig]
  );

  useEffect(() => {
    const pendingPersist = pendingPersistRef.current;
    if (!pendingPersist) {
      return;
    }
    if (!areColumnConfigsEqual(columnConfig, pendingPersist)) {
      return;
    }
    pendingPersistRef.current = null;
    persistColumnConfig(pendingPersist);
  }, [columnConfig, persistColumnConfig]);

  const updateColumnConfig = (
    updater: (current: PersistedColumnConfig[]) => PersistedColumnConfig[]
  ): void => {
    setColumnConfig((current) => {
      const nextConfig = normalizeColumnConfig(updater(current), orderedDefaults);
      if (areColumnConfigsEqual(current, nextConfig)) {
        return current;
      }
      pendingPersistRef.current = nextConfig;
      if (columnStateMode === "url") {
        pendingUrlSyncRef.current = nextConfig;
      }
      return nextConfig;
    });
  };

  const handleTableChange: TableProps<T>["onChange"] = (
    paginationConfig,
    filters,
    sorter,
    extra
  ) => {
    tableProps?.onChange?.(paginationConfig, filters, sorter, extra);
    onChange?.(paginationConfig, filters, sorter, extra);
  };

  return (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <ColumnManager
          columns={managerColumns}
          onToggle={(key, visible) => {
            updateColumnConfig((current) =>
              current.map((column) =>
                column.key === key ? { ...column, visible } : column
              )
            );
          }}
          onReorder={(sourceKey, targetKey) => {
            updateColumnConfig((current) => {
              const sourceIndex = current.findIndex((column) => column.key === sourceKey);
              const targetIndex = current.findIndex((column) => column.key === targetKey);
              if (sourceIndex === -1 || targetIndex === -1) {
                return current;
              }
              const next = [...current];
              const [source] = next.splice(sourceIndex, 1);
              next.splice(targetIndex, 0, source);
              return next;
            });
          }}
          onShowAll={() => {
            updateColumnConfig((current) =>
              current.map((column) => ({ ...column, visible: true }))
            );
          }}
          onHideAll={() => {
            updateColumnConfig((current) =>
              current.map((column) => ({ ...column, visible: false }))
            );
          }}
          onResetDefaults={() => {
            updateColumnConfig(() => orderedDefaults);
          }}
        />
      </div>

      <Table<T>
        {...tableProps}
        rowKey={rowKey}
        columns={visibleColumns}
        dataSource={dataSource ?? tableProps?.dataSource}
        loading={loading ?? tableProps?.loading}
        pagination={pagination ?? tableProps?.pagination}
        scroll={scroll}
        expandable={expandable}
        onChange={handleTableChange}
      />
    </Space>
  );
}
